import React, { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Box, Typography, Paper, useTheme } from '@mui/material';

/**
 * Calculate the intrinsic value of an option leg at a given underlying price.
 * @param {object} leg - { optionInfo: { strike, type }, quantity, cost_basis }
 * @param {number} price - underlying price
 * @returns {number} intrinsic value (before subtracting cost)
 */
const legIntrinsicAt = (leg, price) => {
  const strike = leg.optionInfo?.strike || 0;
  const type = leg.optionInfo?.type || 'C';
  const qty = leg.quantity || 0;
  const absQty = Math.abs(qty);
  const isLong = qty > 0;
  const multiplier = 100;

  if (type === 'C') {
    const intrinsic = Math.max(0, price - strike) * multiplier * absQty;
    return isLong ? intrinsic : -intrinsic;
  } else {
    const intrinsic = Math.max(0, strike - price) * multiplier * absQty;
    return isLong ? intrinsic : -intrinsic;
  }
};

/**
 * Calculate net debit/credit paid for the position.
 * cost_basis is signed: positive for long (debit), negative for short (credit).
 * @param {array} legs
 * @returns {number} net debit (positive) or net credit (negative)
 */
const calcNetDebit = (legs) => {
  return legs.reduce((sum, leg) => sum + (leg.cost_basis || 0), 0);
};

/**
 * Generate payoff data points across a range of underlying prices.
 */
const generatePayoffData = (legs, currentPrice, numPoints = 121) => {
  const strikes = legs.map(l => l.optionInfo?.strike || 0).filter(s => s > 0);
  if (strikes.length === 0) return [];

  const minStrike = Math.min(...strikes);
  const maxStrike = Math.max(...strikes);
  const netDebit = calcNetDebit(legs);
  const absQty = Math.abs(legs[0]?.quantity || 1);

  // Range: from below lowest strike to above highest strike
  const range = Math.max(maxStrike - minStrike, currentPrice * 0.1);
  const padding = range * 0.3;
  const minPrice = Math.max(0, minStrike - padding);
  const maxPrice = maxStrike + padding;
  const step = (maxPrice - minPrice) / (numPoints - 1);

  const data = [];
  for (let i = 0; i < numPoints; i++) {
    const price = minPrice + step * i;
    const totalIntrinsic = legs.reduce((sum, leg) => sum + legIntrinsicAt(leg, price), 0);
    const pl = totalIntrinsic - netDebit;
    data.push({
      price: parseFloat(price.toFixed(2)),
      pl: parseFloat(pl.toFixed(2)),
      isProfit: pl >= 0,
    });
  }
  return data;
};

/**
 * Calculate breakeven points for common strategies.
 */
const findBreakevens = (data) => {
  const breakevens = [];
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1];
    const curr = data[i];
    if ((prev.pl < 0 && curr.pl >= 0) || (prev.pl > 0 && curr.pl <= 0)) {
      // Linear interpolation
      const ratio = Math.abs(prev.pl) / (Math.abs(prev.pl) + Math.abs(curr.pl));
      const bePrice = prev.price + ratio * (curr.price - prev.price);
      breakevens.push(parseFloat(bePrice.toFixed(2)));
    }
  }
  return breakevens;
};

/**
 * Calculate max profit, max loss from payoff data.
 */
const calcMaxProfitLoss = (data) => {
  let maxProfit = -Infinity;
  let maxLoss = Infinity;
  let maxProfitPrice = 0;
  let maxLossPrice = 0;

  data.forEach(point => {
    if (point.pl > maxProfit) {
      maxProfit = point.pl;
      maxProfitPrice = point.price;
    }
    if (point.pl < maxLoss) {
      maxLoss = point.pl;
      maxLossPrice = point.price;
    }
  });

  return {
    maxProfit: maxProfit === -Infinity ? 0 : maxProfit,
    maxLoss: maxLoss === Infinity ? 0 : maxLoss,
    maxProfitPrice,
    maxLossPrice,
  };
};

const PayoffDiagram = ({ legs, spreadType, underlyingPrice, underlyingSymbol, animated = true }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const profitColor = isDark ? '#388e3c' : '#4caf50';
  const lossColor = isDark ? '#d32f2f' : '#f44336';
  const axisColor = isDark ? '#9e9e9e' : '#999';
  const profitFill = isDark ? 'rgba(56, 142, 60, 0.3)' : 'rgba(76, 175, 80, 0.15)';
  const lossFill = isDark ? 'rgba(211, 47, 47, 0.3)' : 'rgba(244, 67, 54, 0.15)';

  const data = useMemo(() => generatePayoffData(legs, underlyingPrice), [legs, underlyingPrice]);
  const breakevens = useMemo(() => findBreakevens(data), [data]);
  const { maxProfit, maxLoss } = useMemo(() => calcMaxProfitLoss(data), [data]);
  const netDebit = useMemo(() => calcNetDebit(legs), [legs]);
  const currentPL = useMemo(() => {
    if (!underlyingPrice || !legs || legs.length === 0) return null;
    const totalIntrinsic = legs.reduce((sum, leg) => sum + legIntrinsicAt(leg, underlyingPrice), 0);
    return totalIntrinsic - netDebit;
  }, [legs, underlyingPrice, netDebit]);

  if (!data.length || !legs || legs.length < 1) return null;

  // Split data into profit and loss segments for area coloring
  const chartData = data.map(point => ({
    ...point,
    profitArea: point.pl >= 0 ? point.pl : 0,
    lossArea: point.pl < 0 ? point.pl : 0,
  }));

  const formatCurrency = (val) => {
    if (val === 0) return '$0';
    const sign = val >= 0 ? '+' : '-';
    return `${sign}$${Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const isCredit = netDebit < 0;
  const strikes = legs.map(l => l.optionInfo?.strike || 0).filter(s => s > 0).sort((a, b) => a - b);

  return (
    <Paper elevation={3} sx={{ p: 1.5, width: '100%', maxHeight: 340, flex: 1, bgcolor: isDark ? 'grey.800' : 'background.paper' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.85rem' }}>
        {spreadType} — {underlyingSymbol || ''}
      </Typography>

      {/* Summary stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
          <strong>Max Profit:</strong>{' '}
          <span style={{ color: maxProfit > 0 ? profitColor : (isDark ? '#888' : '#666') }}>
            {maxProfit === Infinity ? 'Unlimited' : formatCurrency(maxProfit)}
          </span>
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
          <strong>Max Loss:</strong>{' '}
          <span style={{ color: maxLoss < 0 ? lossColor : (isDark ? '#888' : '#666') }}>
            {formatCurrency(maxLoss)}
          </span>
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
          <strong>{isCredit ? 'Credit' : 'Debit'}:</strong> ${Math.abs(netDebit).toFixed(2)}
        </Typography>
        {breakevens.map((be, i) => (
          <Typography key={`be-${i}`} variant="caption" sx={{ fontSize: '0.7rem' }}>
            <strong>BE{i + 1}:</strong> ${be.toFixed(2)}
          </Typography>
        ))}
        {currentPL !== null && (
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            <strong>Now:</strong>{' '}
            <span style={{ color: currentPL >= 0 ? profitColor : lossColor }}>
              {formatCurrency(currentPL)} @ ${underlyingPrice.toFixed(2)}
            </span>
          </Typography>
        )}
      </Box>

      {/* Payoff chart */}
      <Box sx={{ width: '100%', height: 180, minHeight: 180, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={180} minWidth={0}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 15, left: 0 }}>
            <XAxis
              dataKey="price"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(v) => `$${v.toFixed(0)}`}
              tick={{ fontSize: 10 }}
              label={{ value: 'Underlying Price', position: 'insideBottom', offset: -5, style: { fontSize: 10 } }}
            />
            <YAxis
              tickFormatter={(v) => formatCurrency(v)}
              tick={{ fontSize: 10 }}
              width={60}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === 'pl') return [formatCurrency(value), 'P/L'];
                return [value, name];
              }}
              labelFormatter={(label) => `Price: $${label}`}
              contentStyle={{
                fontSize: '0.75rem',
                backgroundColor: isDark ? '#424242' : '#fff',
                color: isDark ? '#fff' : '#333',
                border: `1px solid ${isDark ? '#666' : '#ccc'}`,
              }}
              labelStyle={{ color: isDark ? '#ccc' : '#666' }}
              itemStyle={{ color: isDark ? '#fff' : '#333' }}
            />
            <ReferenceLine y={0} stroke={axisColor} strokeWidth={1} />

            {/* Strike lines */}
            {strikes.map((strike, i) => (
              <ReferenceLine
                key={`strike-${i}`}
                x={strike}
                stroke={axisColor}
                strokeDasharray="2 2"
                label={{ value: `K${strike}`, position: 'top', style: { fontSize: 9, fill: axisColor } }}
              />
            ))}

            {/* Current price line — bold, prominent */}
            {underlyingPrice && (
              <ReferenceLine
                x={underlyingPrice}
                stroke={currentPL >= 0 ? profitColor : lossColor}
                strokeWidth={3}
                label={{ value: `$${underlyingPrice.toFixed(2)}`, position: 'top', style: { fontSize: 11, fill: currentPL >= 0 ? profitColor : lossColor, fontWeight: 'bold' } }}
              />
            )}

            {/* Breakeven lines */}
            {breakevens.map((be, i) => (
              <ReferenceLine
                key={`be-line-${i}`}
                x={be}
                stroke="#ff9800"
                strokeDasharray="4 2"
                label={{ value: 'BE', position: 'top', style: { fontSize: 9, fill: '#ff9800' } }}
              />
            ))}

            {/* Profit area (green) */}
            <Area
              dataKey="profitArea"
              fill={profitFill}
              stroke="none"
              connectNulls
              isAnimationActive={animated}
            />
            {/* Loss area (red) */}
            <Area
              dataKey="lossArea"
              fill={lossFill}
              stroke="none"
              connectNulls
              isAnimationActive={animated}
            />
            {/* P/L line */}
            <Line
              type="linear"
              dataKey="pl"
              stroke="#1976d2"
              strokeWidth={2}
              dot={false}
              isAnimationActive={animated}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default PayoffDiagram;
